import { Skeleton } from "primereact/skeleton";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { GET_DASHBOARD_DATA } from "../../config/ApiConstant";
import { Chart } from "primereact/chart";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { getSpecificItemDetails } from "../../service/ProductService";
import moment from "moment";
import { Messages } from "primereact/messages";
import { Dialog } from "primereact/dialog";
import UpdatePaymentMethod from "../../components/BankDetails/BankDetails";
import { Button } from "primereact/button";

const Dashboard = () => {
  document.title = "Dashboard | Nexawage Payroll";
  const toast = useRef<any>(null);

  const navigate = useNavigate();

  // get userData from local storage
  const rawUserData = localStorage.getItem("user_data");
  let userData: any = null;
  userData = rawUserData ? JSON.parse(rawUserData) : null;
  const userRole = userData?.roles?.role_name || "";

  // dashboard data obj
  const [dashboardData, setDashboardData] = useState<any>({});

  // loader
  const [pageLoad, setPageLoad] = useState<boolean>(false);

  // graph obj
  const [barOptions, setBarOptions] = useState<any>(null);
  const [barChartData, setBarChartData] = useState<any>(null);

  // year obj
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date());

  // message ref
  const msgs = useRef<any>(null);

  //  Modal state for first-time visit
  const [showBankModal, setShowBankModal] = useState(false);

  // stripe card detail form model
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // based on user role dashboard detail
  useEffect(() => {
    if ((userRole === "Client" || userRole === "Employee") && selectedYear) {
      getDashboard(moment(selectedYear).year());
    } else {
      getDashboard();
    }
  }, [selectedYear, userRole]);

  // bar chart
  useEffect(() => {
    prepareGrossPaymentChart(dashboardData, setBarChartData, setBarOptions);
  }, [dashboardData]);

  // use Effect to display message to add bank details
  useEffect(() => {
    if (userRole === "User" || userRole === "Client") {
      if (msgs.current) {
        msgs.current.show({
          sticky: true,
          severity: "info",
          summary: "Click here to add card details",
          detail: "",
          closable: false,
        });
      }
    }
  }, []);

  // Check if user added bank detail or not
  useEffect(() => {
    if (!pageLoad) return;

    const model_status = localStorage.getItem("payment_model_status");

    // check if user has added bank details or not
    const bank_detail_added = dashboardData?.payment_details_added;

    // Show modal only if bank details NOT added
    if (model_status !== "true" && bank_detail_added !== true) {
      if (userRole === "User") {
        setShowBankModal(true);
      }
    }
  }, [dashboardData, pageLoad]);

  // display bar chart values
  const prepareGrossPaymentChart = (
    dashboardData: any,
    setBarChartData: (data: any) => void,
    setBarOptions: (options: any) => void
  ) => {
    const payments = dashboardData?.gross_payment || [];
    if (payments.length > 0) {
      const values = payments.map((item: any) =>
        parseFloat(item.gross_payment)
      );

      // label format
      // const labels = payments.map((item: any) => {
      //   const start = moment(item?.start_date);
      //   const end = moment(item?.end_date);

      //   const sameMonth = start.month() === end.month();

      //   if (sameMonth) {
      //     return `${start.format("MMM D")} - ${end.format("D")}`;
      //   } else {
      //     return `${start.format("MMM D")} - ${end.format("MMM D")}`;
      //   }
      // });

      const labels = payments.map((item: any) => item.month);

      // bar chart data
      const dynamicBarData = {
        labels,
        datasets: [
          {
            label: "Gross Payment",
            backgroundColor: "rgb(54, 162, 235)",
            borderColor: "rgb(54, 162, 235)",
            data: values,
          },
        ],
      };

      // bar chart option
      const dynamicBarOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        responsive: true,
        plugins: {
          legend: {
            position: "top",
          },
          title: {
            display: true,
            text: "Gross Payments Over Time",
          },
          tooltip: {
            callbacks: {
              label: (ctx: any) => `$${ctx.raw?.toFixed(2) || 0}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value: number) => `$${value}`,
            },
          },
          x: {
            ticks: {
              autoSkip: true,
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
      };

      setBarChartData(dynamicBarData);
      setBarOptions(dynamicBarOptions);
    } else {
      setBarChartData(null);
      setBarOptions(null);
    }
  };

  // get dashboard data
  const getDashboard = async (year = currentYear) => {
    try {
      const response = await getSpecificItemDetails(
        GET_DASHBOARD_DATA,
        "year",
        year
      );
      const data = response.data;

      if (data) {
        setDashboardData(data);
        setPageLoad(true);

        // set local storage with card detail added or not (flag)
        const payment_details_added = data?.payment_details_added;
        localStorage.setItem("payment_details_added", payment_details_added);
      } else {
        setDashboardData({});
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  return (
    <>
      <AppBreadcrumb
        currentMenu={"Dashboard"}
        pageName={"Dashboard"}
        pageTitle="Dashboard"
      />

      <Toast ref={toast} />
      <div className="layout-dashboard">
        {(userRole === "Client" ||
          userRole === "Employee" ||
          userRole === "User") && (
          <div
            className={`flex align-items-center ${
              userRole === "User" || userRole === "Client"
                ? "justify-content-between"
                : "justify-content-end"
            }`}
          >
            {(userRole === "User" || userRole === "Client") && (
              <div className="app-messages mb-0">
                <Messages
                  onClick={() => setShowPaymentModal(true)}
                  className="mb-0"
                  ref={msgs}
                />
              </div>
            )}
            <Calendar
              id="year"
              value={selectedYear}
              onChange={(e) => {
                if (e.value instanceof Date) {
                  setSelectedYear(e.value);
                } else {
                  setSelectedYear(null);
                }
              }}
              view="year"
              dateFormat="yy"
              showIcon
              className="w-auto"
              placeholder="Select Year"
            />
          </div>
        )}
        <div className="grid mt-2">
          {/* Total Users (Admin only) */}
          {userRole === "Admin" && (
            <>
              <div className="col-12 md:col-6 lg:col-4">
                <Link to="/users">
                  <div className="card dashbaord-main-box">
                    <div className="small-box bg-info">
                      <div
                        className="inner"
                        style={{ borderLeft: "3px solid #dc3545" }}
                      >
                        <div className="flex flex-column justify-content-between">
                          <h2 className="mb-1 title-text">Total Users</h2>
                          <div className="flex align-items-center justify-content-between">
                            <h3
                              className="mt-0 mb-0 num-text"
                              style={{ color: "#dc3545" }}
                            >
                              {pageLoad ? (
                                !window.cn(dashboardData) &&
                                !window.cn(dashboardData.user_count) ? (
                                  dashboardData.user_count
                                ) : (
                                  ""
                                )
                              ) : (
                                <Skeleton
                                  width="5rem"
                                  height="2.5rem"
                                  className="rounded"
                                />
                              )}
                            </h3>
                            <div
                              className="dashboard-icon"
                              style={{
                                padding: "0.35rem",
                                backgroundColor: "#f8d7da",
                                borderRadius: "9999px",
                                color: "#dc3545",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                className="lucide lucide-users"
                              >
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <path d="M16 3.128a4 4 0 0 1 0 7.744" />
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                                <circle cx="9" cy="7" r="4" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </>
          )}

          {/* Total Clients (Admin only) */}
          {(userRole === "Admin" || userRole === "User") && (
            <div className="col-12 md:col-6 lg:col-4">
              <Link to="/clients">
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div
                      className="inner"
                      style={{ borderLeft: "3px solid #35b653" }}
                    >
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Total Clients</h2>
                        <div className="flex align-items-center justify-content-between">
                          <h3
                            className="mt-0 mb-0 num-text"
                            style={{ color: "#35b653" }}
                          >
                            {pageLoad ? (
                              !window.cn(dashboardData) ? (
                                userRole === "User" &&
                                !window.cn(dashboardData.users_client_count) ? (
                                  dashboardData.users_client_count
                                ) : userRole === "Admin" &&
                                  !window.cn(dashboardData.client_count) ? (
                                  dashboardData.client_count
                                ) : (
                                  ""
                                )
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton
                                width="5rem"
                                height="2.5rem"
                                className="rounded"
                              />
                            )}
                          </h3>
                          <div
                            className="dashboard-icon"
                            style={{
                              padding: "0.35rem",
                              backgroundColor: "#d7f0dd",
                              borderRadius: "9999px",
                              color: "#35b653",
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              className="lucide lucide-building2"
                            >
                              <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                              <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
                              <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
                              <path d="M10 6h4" />
                              <path d="M10 10h4" />
                              <path d="M10 14h4" />
                              <path d="M10 18h4" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Total Employees (All roles) client */}
          {userRole === "Client" && (
            <div className="col-12 md:col-6 lg:col-4">
              <Link to={"/employees"}>
                <div className="card dashbaord-main-box">
                  <div className="small-box bg-info">
                    <div
                      className="inner"
                      style={{ borderLeft: "3px solid #0d6efd" }}
                    >
                      <div className="flex flex-column justify-content-between">
                        <h2 className="mb-1 title-text">Total Employees</h2>
                        <div className="flex align-items-center justify-content-between">
                          <h3
                            className="mt-0 mb-0 num-text"
                            style={{ color: "#0d6efd" }}
                          >
                            {pageLoad ? (
                              !window.cn(dashboardData) ? (
                                userRole === "Client" &&
                                !window.cn(
                                  dashboardData.client_employee_Count
                                ) ? (
                                  dashboardData.client_employee_Count
                                ) : userRole === "Admin" &&
                                  !window.cn(dashboardData.emp_count) ? (
                                  dashboardData.emp_count
                                ) : (
                                  ""
                                )
                              ) : (
                                ""
                              )
                            ) : (
                              <Skeleton
                                width="5rem"
                                height="2.5rem"
                                className="rounded"
                              />
                            )}
                          </h3>
                          <div
                            className="dashboard-icon relative"
                            style={{
                              padding: "0.35rem",
                              backgroundColor: "#e7f0ff",
                              borderRadius: "9999px",
                              color: "#0d6efd",
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-user-check absolute"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginLeft: "3px",
                              }}
                              viewBox="0 0 24 24"
                            >
                              <path d="m17 12 2 2 4-4" />
                              <path d="M16 22v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                              <circle cx="10.5" cy="8" r="3" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Total Employees (All roles) client */}
          {userRole === "Admin" && (
            <div className="col-12 md:col-6 lg:col-4">
              <div className="card dashbaord-main-box">
                <div className="small-box bg-info">
                  <div
                    className="inner"
                    style={{ borderLeft: "3px solid #0d6efd" }}
                  >
                    <div className="flex flex-column justify-content-between">
                      <h2 className="mb-1 title-text">Total Employees</h2>
                      <div className="flex align-items-center justify-content-between">
                        <h3
                          className="mt-0 mb-0 num-text"
                          style={{ color: "#0d6efd" }}
                        >
                          {pageLoad ? (
                            !window.cn(dashboardData) ? (
                              userRole === "Client" &&
                              !window.cn(
                                dashboardData.client_employee_Count
                              ) ? (
                                dashboardData.client_employee_Count
                              ) : userRole === "Admin" &&
                                !window.cn(dashboardData.emp_count) ? (
                                dashboardData.emp_count
                              ) : (
                                ""
                              )
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              width="5rem"
                              height="2.5rem"
                              className="rounded"
                            />
                          )}
                        </h3>
                        <div
                          className="dashboard-icon"
                          style={{
                            padding: "0.35rem",
                            backgroundColor: "#e7f0ff",
                            borderRadius: "9999px",
                            color: "#0d6efd",
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            className="lucide lucide-user-check"
                          >
                            <path d="m16 11 2 2 4-4" />
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* if user role is employee then total pay stub should display */}
          {userRole === "Employee" && (
            <>
              <div className="col-12 md:col-6 lg:col-4">
                <Link to="/paystub">
                  <div className="card dashbaord-main-box">
                    <div className="small-box bg-info">
                      <div
                        className="inner"
                        style={{ borderLeft: "3px solid #4650dd" }}
                      >
                        <div className="flex flex-column justify-content-between">
                          <h2 className="mb-1 title-text">Total PayStub</h2>
                          <div className="flex align-items-center justify-content-between">
                            <h3
                              className="mt-0 mb-0 num-text"
                              style={{ color: "#4650dd" }}
                            >
                              {pageLoad ? (
                                !window.cn(dashboardData) &&
                                !window.cn(
                                  dashboardData.employee_pay_stub_count
                                ) ? (
                                  dashboardData.employee_pay_stub_count
                                ) : (
                                  ""
                                )
                              ) : (
                                <Skeleton
                                  width="5rem"
                                  height="2.5rem"
                                  className="rounded"
                                />
                              )}
                            </h3>
                            <div
                              className="dashboard-icon"
                              style={{
                                padding: "0.5rem",
                                backgroundColor: "#dadcf8",
                                borderRadius: "9999px",
                                color: "#4650dd",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                className="lucide lucide-file-text"
                              >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <line x1="10" y1="9" x2="8" y2="9" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </>
          )}

          {/* Total Gross Payment client */}
          {userRole === "Client" && (
            <>
              <div className="col-12 md:col-6 lg:col-4">
                <Link to={"/pd7a-report"}>
                  <div className="card dashbaord-main-box">
                    <div className="small-box bg-info">
                      <div
                        className="inner"
                        style={{ borderLeft: "3px solid #35b653" }}
                      >
                        <div className="flex flex-column justify-content-between ">
                          <h2 className="mb-1 title-text">
                            Total Gross Payment
                          </h2>
                          <div className="flex align-items-center justify-content-between">
                            <h3
                              className="mt-0 mb-0 num-text"
                              style={{ color: "#35b653" }}
                            >
                              {pageLoad ? (
                                !window.cn(dashboardData) ? (
                                  userRole === "Client" &&
                                  !window.cn(
                                    dashboardData.yearly_gross_payment
                                  ) ? (
                                    `$${dashboardData.yearly_gross_payment}`
                                  ) : (
                                    ""
                                  )
                                ) : (
                                  ""
                                )
                              ) : (
                                <Skeleton
                                  width="5rem"
                                  height="2.5rem"
                                  className="rounded"
                                />
                              )}
                            </h3>
                            <div
                              className="dashboard-icon"
                              style={{
                                padding: "0.35rem",
                                backgroundColor: "#e8fff2",
                                borderRadius: "9999px",
                                color: "#35b653",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                className="lucide lucide-dollar-sign-circle"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v12" />
                                <path d="M16 8H10a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
              <div className="col-12 md:col-6 lg:col-4">
                <Link to={"/pd7a-report"}>
                  <div className="card dashbaord-main-box">
                    <div className="small-box bg-info">
                      <div
                        className="inner"
                        style={{ borderLeft: "3px solid #dc3545" }}
                      >
                        <div className="flex flex-column justify-content-between">
                          <h2 className="mb-1 title-text">Total Tax</h2>
                          <div className="flex align-items-center justify-content-between">
                            <h3
                              className="mt-0 mb-0 num-text"
                              style={{ color: "#dc3545" }}
                            >
                              {pageLoad ? (
                                !window.cn(dashboardData) ? (
                                  userRole === "Client" &&
                                  !window.cn(dashboardData.yearly_total_tax) ? (
                                    `$${dashboardData.yearly_total_tax}`
                                  ) : (
                                    ""
                                  )
                                ) : (
                                  ""
                                )
                              ) : (
                                <Skeleton
                                  width="5rem"
                                  height="2.5rem"
                                  className="rounded"
                                />
                              )}
                            </h3>
                            <div
                              className="dashboard-icon"
                              style={{
                                padding: "0.35rem",
                                backgroundColor: "#ffe8ea",
                                borderRadius: "9999px",
                                color: "#dc3545",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-dollar-sign-circle"
                              >
                                <circle cx="12" cy="12" r="9" />
                                <path d="M12 6v12" />
                                <path d="M16 8H10a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </>
          )}
        </div>

        {/* if userrole is client or employee then bar graph will visible */}
        {(userRole === "Client" || userRole === "Employee") && (
          <div className="grid">
            <div className="col-12">
              <div className="card bar-chart-wrapper">
                <div className="flex justify-content-between align-items-center">
                  <h5 className="m-0">Gross Payment</h5>
                </div>

                {pageLoad ? (
                  barChartData ? (
                    <Chart
                      type="line"
                      data={barChartData}
                      options={barOptions}
                    />
                  ) : (
                    <div className="flex gap-1 p-4 align-items-center flex-column justify-content-center ">
                      <img
                        src="https://res.cloudinary.com/dpc2muwar/image/upload/v1753697918/no-datas_voktda.png"
                        alt=""
                        width="120"
                        height="120"
                        style={{ marginBottom: "-8px" }}
                      />
                      <p className="text-gray-500">
                        No Gross Payment data available for the selected year.
                      </p>
                    </div>
                  )
                ) : (
                  <Skeleton height="300px" className="mt-3 mb-3 border-round" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* add bank details model */}
      <Dialog
        header="Add Card Details"
        visible={showBankModal}
        onHide={() => {
          setShowBankModal(false);
          localStorage.setItem("payment_model_status", "true");
        }}
        modal
        draggable={false}
        dismissableMask={false}
        className="bank-details-modal"
        style={{ width: "30rem" }}
        footer={<div></div>}
      >
        <p className="m-0 text-center">
          It looks like you haven't added your card details yet.
        </p>

        <div className="flex justify-content-center mt-4">
          <button
            className="bank-modal-btn"
            onClick={() => {
              setShowBankModal(false);
              setShowPaymentModal(true);
              localStorage.setItem("payment_model_status", "true");
            }}
          >
            Add Card Details
          </button>
        </div>
      </Dialog>

      {/* dialog for stripe card detail form */}
      <Dialog
        header="Add Card Details"
        visible={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        modal
        draggable={false}
        dismissableMask={false}
        style={{ width: "35rem" }}
        footer={<div></div>}
      >
        <UpdatePaymentMethod
          customerId={userData?.customer_id}
          onSuccess={() => setShowPaymentModal(false)}
          getApiCall={() => getDashboard()}
        />
      </Dialog>
    </>
  );
};

export default Dashboard;
